import { useState, useMemo, useCallback } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  MiniMap,
  Background,
  BackgroundVariant,
  useReactFlow,
} from "@xyflow/react";
import type { NodeMouseHandler } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import type { PacketGraphDetail } from "../../types/shadow-run";
import type { PacketGraphNode } from "../../types/shadow-run";
import { CalculationNode } from "./CalculationNode";
import { CalculationEdge } from "./CalculationEdge";
import { GraphControls } from "./GraphControls";
import { ImpactTracePanel } from "./ImpactTracePanel";
import { useGraphLayout } from "./useGraphLayout";
import {
  toReactFlowGraph,
  findVariancePaths,
  traceNodePath,
  filterByDepth,
  filterToVariancePaths,
  computeMaxDepth,
  computeImpactTrace,
} from "./graphUtils";
import type { CalculationNodeData } from "./graphUtils";
import styles from "./CalculationGraph.module.css";

const NODE_TYPES = { calculationNode: CalculationNode };
const EDGE_TYPES = { calculationEdge: CalculationEdge };

export interface CalculationGraphProps {
  graph: PacketGraphDetail;
}

function CalculationGraphInner({ graph }: CalculationGraphProps) {
  const { fitView } = useReactFlow();

  const [depth, setDepth] = useState(Infinity);
  const [varianceOnly, setVarianceOnly] = useState(false);
  const [showMinimap, setShowMinimap] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(
    new Set(),
  );
  const [panelNode, setPanelNode] = useState<PacketGraphNode | null>(null);

  // Resolve effective root: prefer singular, fall back to first of array, or null
  const effectiveRootId =
    graph.root_node_id ?? graph.root_node_ids[0] ?? null;

  // Step 1: Convert API data to ReactFlow format
  const { nodes: rawNodes, edges: rawEdges } = useMemo(
    () => toReactFlowGraph(graph),
    [graph],
  );

  // Step 2: Find variance paths
  const { varianceNodeIds, varianceEdgeIds } = useMemo(
    () => findVariancePaths(rawNodes, rawEdges, effectiveRootId),
    [rawNodes, rawEdges, effectiveRootId],
  );

  // Step 3: Compute max depth
  const maxDepth = useMemo(
    () =>
      computeMaxDepth(
        rawNodes,
        rawEdges,
        effectiveRootId,
        graph.root_node_ids,
      ),
    [rawNodes, rawEdges, effectiveRootId, graph.root_node_ids],
  );

  // Use actual max depth as default when Infinity
  const effectiveDepth = depth === Infinity ? maxDepth : depth;

  // Step 4: Apply filters
  const { nodes: filteredNodes, edges: filteredEdges } = useMemo(() => {
    let result = filterByDepth(
      rawNodes,
      rawEdges,
      effectiveRootId,
      effectiveDepth,
      graph.root_node_ids,
    );

    if (varianceOnly) {
      result = filterToVariancePaths(
        result.nodes,
        result.edges,
        varianceNodeIds,
        varianceEdgeIds,
      );
    }

    return result;
  }, [
    rawNodes,
    rawEdges,
    effectiveRootId,
    effectiveDepth,
    graph.root_node_ids,
    varianceOnly,
    varianceNodeIds,
    varianceEdgeIds,
  ]);

  // Step 5: Compute highlights from selected nodes
  const { highlightedNodeIds, highlightedEdgeIds } = useMemo(() => {
    const hNodeIds = new Set<string>();
    const hEdgeIds = new Set<string>();

    for (const nodeId of selectedNodeIds) {
      const traced = traceNodePath(nodeId, filteredEdges);
      for (const id of traced.nodeIds) hNodeIds.add(id);
      for (const id of traced.edgeIds) hEdgeIds.add(id);
    }

    return { highlightedNodeIds: hNodeIds, highlightedEdgeIds: hEdgeIds };
  }, [selectedNodeIds, filteredEdges]);

  // Step 5b: Compute impact trace for the selected panel node
  const impactTrace = useMemo(() => {
    if (!panelNode) return [];
    const { nodeIds, edgeIds } = traceNodePath(
      panelNode.node_id,
      filteredEdges,
    );
    return computeImpactTrace(nodeIds, edgeIds, filteredNodes, filteredEdges);
  }, [panelNode, filteredNodes, filteredEdges]);

  // Step 6: Apply visual state to nodes and edges
  const styledNodes = useMemo(
    () =>
      filteredNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isSelected: selectedNodeIds.has(node.id),
          isHighlighted:
            !selectedNodeIds.has(node.id) && highlightedNodeIds.has(node.id),
          isOnVariancePath: varianceNodeIds.has(node.id),
        },
      })),
    [filteredNodes, selectedNodeIds, highlightedNodeIds, varianceNodeIds],
  );

  const styledEdges = useMemo(
    () =>
      filteredEdges.map((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          isOnVariancePath: varianceEdgeIds.has(edge.id),
          isHighlighted: highlightedEdgeIds.has(edge.id),
        },
      })),
    [filteredEdges, varianceEdgeIds, highlightedEdgeIds],
  );

  // Step 7: Layout with dagre
  const { nodes: layoutNodes, edges: layoutEdges } = useGraphLayout(
    styledNodes,
    styledEdges,
  );

  // Build lookup for panel
  const nodeMap = useMemo(() => {
    const map = new Map<string, PacketGraphNode>();
    for (const n of graph.nodes) {
      map.set(n.node_id, n);
    }
    return map;
  }, [graph.nodes]);

  const handleNodeClick: NodeMouseHandler<CalculationNodeData> = useCallback(
    (event, node) => {
      const isShift = event.shiftKey;

      setSelectedNodeIds((prev) => {
        if (isShift) {
          const next = new Set(prev);
          if (next.has(node.id)) {
            next.delete(node.id);
          } else {
            next.add(node.id);
          }
          return next;
        }
        return new Set([node.id]);
      });

      const apiNode = nodeMap.get(node.id);
      if (apiNode && !event.shiftKey) {
        setPanelNode(apiNode);
      }
    },
    [nodeMap],
  );

  const handlePaneClick = useCallback(() => {
    setSelectedNodeIds(new Set());
    setPanelNode(null);
  }, []);

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.1 });
  }, [fitView]);

  const handleClosePanel = useCallback(() => {
    setPanelNode(null);
  }, []);

  return (
    <div className={styles.container}>
      <GraphControls
        depth={effectiveDepth}
        maxDepth={maxDepth}
        onDepthChange={setDepth}
        varianceOnly={varianceOnly}
        onVarianceOnlyChange={setVarianceOnly}
        showMinimap={showMinimap}
        onShowMinimapChange={setShowMinimap}
        onFitView={handleFitView}
      />
      <div className={styles.graphArea}>
        {varianceOnly && varianceNodeIds.size === 0 ? (
          <div className={styles.emptyState}>
            <p>No variance detected in this packet's calculation graph.</p>
          </div>
        ) : (
          <ReactFlow
            nodes={layoutNodes}
            edges={layoutEdges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            onNodeClick={handleNodeClick}
            onPaneClick={handlePaneClick}
            fitView
            minZoom={0.1}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
            {showMinimap && (
              <MiniMap
                nodeStrokeWidth={3}
                zoomable
                pannable
              />
            )}
          </ReactFlow>
        )}
        {panelNode && (
          <ImpactTracePanel
            trace={impactTrace}
            selectedNodeId={panelNode.node_id}
            onClose={handleClosePanel}
          />
        )}
      </div>
    </div>
  );
}

export function CalculationGraph({ graph }: CalculationGraphProps) {
  return (
    <ReactFlowProvider>
      <CalculationGraphInner graph={graph} />
    </ReactFlowProvider>
  );
}
